import React, { useState, useEffect, useRef } from 'react';
import { FileText, Upload, Trash2, Download, Eye, X, File, Image, AlertCircle, CheckCircle, Clock, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DocumentManager = ({ patientId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);

  const [uploadData, setUploadData] = useState({
    document_type: 'tessera_sanitaria',
    description: '',
    file: null
  });

  const DOCUMENT_TYPES = [
    { value: 'carta_identita', label: 'Carta d\'Identità', icon: '🪪' },
    { value: 'tessera_sanitaria', label: 'Tessera Sanitaria', icon: '💳' },
    { value: 'codice_fiscale', label: 'Codice Fiscale', icon: '📄' },
    { value: 'patente', label: 'Patente di Guida', icon: '🚗' },
    { value: 'passaporto', label: 'Passaporto', icon: '🛂' },
    { value: 'referto_medico', label: 'Referto Medico', icon: '🏥' },
    { value: 'prescrizione', label: 'Prescrizione', icon: '💊' },
    { value: 'esame_diagnostico', label: 'Esame Diagnostico', icon: '🔬' },
    { value: 'fattura', label: 'Fattura/Ricevuta', icon: '🧾' },
    { value: 'altro', label: 'Altro Documento', icon: '📁' }
  ];

  useEffect(() => {
    fetchDocuments();
  }, [patientId]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/patient/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Il file è troppo grande. Massimo 10MB.');
        return;
      }
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      alert('Seleziona un file da caricare');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('document_type', uploadData.document_type);
      formData.append('description', uploadData.description);

      const res = await fetch(`${API}/api/patient/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        fetchDocuments();
        resetUpload();
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante il caricamento');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Errore durante il caricamento del documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/patient/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/patient/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename || 'documento';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const resetUpload = () => {
    setUploadData({
      document_type: 'tessera_sanitaria',
      description: '',
      file: null
    });
    setShowUploadModal(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getDocTypeInfo = (type) => {
    return DOCUMENT_TYPES.find(d => d.value === type) || { label: type, icon: '📄' };
  };

  const filteredDocuments = selectedCategory === 'all' 
    ? documents 
    : documents.filter(d => d.document_type === selectedCategory);

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2A9FCB]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-semibold" style={{color: '#2A9FCB', fontFamily: "'Lora', Georgia, serif"}}>
            I Miei Documenti
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Carica e gestisci i tuoi documenti sanitari e personali
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all hover:opacity-90"
          style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
          data-testid="upload-document-btn"
        >
          <Upload className="w-4 h-4" />
          Carica Documento
        </button>
      </div>

      {/* Info Box */}
      <div className="p-4 rounded-xl border" style={{background: '#E8F4FD', borderColor: '#2A9FCB'}}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" style={{color: '#2A9FCB'}} />
          <div>
            <p className="text-sm font-medium" style={{color: '#2A9FCB'}}>Documenti Richiesti</p>
            <p className="text-xs text-gray-600 mt-1">
              Per completare la registrazione, carica: Tessera Sanitaria, Documento d'Identità e Codice Fiscale.
              I documenti sono conservati in modo sicuro e accessibili solo al personale autorizzato.
            </p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
            selectedCategory === 'all' ? 'bg-[#2A9FCB] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Tutti ({documents.length})
        </button>
        {DOCUMENT_TYPES.filter(dt => documents.some(d => d.document_type === dt.value)).map(dt => (
          <button
            key={dt.value}
            onClick={() => setSelectedCategory(dt.value)}
            className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
              selectedCategory === dt.value ? 'bg-[#2A9FCB] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {dt.icon} {dt.label}
          </button>
        ))}
      </div>

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-2">
            {selectedCategory === 'all' ? 'Nessun documento caricato' : 'Nessun documento in questa categoria'}
          </p>
          <p className="text-sm text-gray-400">Carica i tuoi documenti per averli sempre a portata di mano</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocuments.map((doc) => {
            const typeInfo = getDocTypeInfo(doc.document_type);
            return (
              <div 
                key={doc.id} 
                className="bg-white rounded-xl border p-4 hover:shadow-lg transition-all group"
                data-testid={`document-${doc.id}`}
              >
                {/* Preview Area */}
                <div className="relative h-32 bg-gray-100 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                  {isImageFile(doc.filename) && doc.thumbnail_url ? (
                    <img 
                      src={doc.thumbnail_url} 
                      alt={doc.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center">
                      <span className="text-4xl">{typeInfo.icon}</span>
                      <p className="text-xs text-gray-400 mt-2">{doc.filename?.split('.').pop()?.toUpperCase()}</p>
                    </div>
                  )}
                  
                  {/* Overlay Actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => setPreviewDoc(doc)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100"
                      title="Anteprima"
                    >
                      <Eye className="w-4 h-4 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleDownload(doc)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100"
                      title="Scarica"
                    >
                      <Download className="w-4 h-4 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="p-2 bg-white rounded-full hover:bg-red-50"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-medium text-sm" style={{color: '#2A9FCB'}}>{typeInfo.label}</h3>
                    {doc.verified ? (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle className="w-3 h-3" /> Verificato
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-600">
                        <Clock className="w-3 h-3" /> In verifica
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate" title={doc.filename}>{doc.filename}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>{new Date(doc.uploaded_at).toLocaleDateString('it-IT')}</span>
                  </div>
                  {doc.description && (
                    <p className="text-xs text-gray-500 mt-2 border-t pt-2">{doc.description}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-semibold" style={{color: '#2A9FCB'}}>Carica Documento</h3>
              <button onClick={resetUpload} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpload} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Documento *</label>
                <select
                  required
                  value={uploadData.document_type}
                  onChange={(e) => setUploadData({...uploadData, document_type: e.target.value})}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                >
                  {DOCUMENT_TYPES.map(dt => (
                    <option key={dt.value} value={dt.value}>{dt.icon} {dt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    uploadData.file ? 'border-[#2A9FCB] bg-[#E8F4FD]' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  />
                  {uploadData.file ? (
                    <div className="flex items-center justify-center gap-3">
                      <File className="w-8 h-8 text-[#2A9FCB]" />
                      <div className="text-left">
                        <p className="font-medium text-[#2A9FCB]">{uploadData.file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(uploadData.file.size)}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Clicca per selezionare un file</p>
                      <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, DOC (max 10MB)</p>
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione (opzionale)</label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                  rows={2}
                  placeholder="Es: Tessera sanitaria - fronte e retro"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetUpload}
                  className="flex-1 py-3 border rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  disabled={uploading}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={!uploadData.file || uploading}
                  className="flex-1 py-3 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Caricamento...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Carica
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold" style={{color: '#2A9FCB'}}>{previewDoc.filename}</h3>
              <button onClick={() => setPreviewDoc(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center bg-gray-100 min-h-[400px]">
              {isImageFile(previewDoc.filename) ? (
                <img 
                  src={previewDoc.file_url || previewDoc.thumbnail_url} 
                  alt={previewDoc.filename}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              ) : (
                <div className="text-center">
                  <File className="w-20 h-20 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Anteprima non disponibile per questo tipo di file</p>
                  <button
                    onClick={() => handleDownload(previewDoc)}
                    className="mt-4 px-4 py-2 rounded-lg text-white"
                    style={{background: '#2A9FCB'}}
                  >
                    Scarica per visualizzare
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManager;
